import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { FavouriteType, FavouritesService, FavouritesViewModel } from '@frontend/sports/favourites/core/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { TrackData } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import { Observable, Subscription, catchError, filter, map, of } from 'rxjs';

import { EpcotConfigService } from '../../common/epcot-config.service';
import { FavouriteToggleTrackingService } from './favourite-toggle-tracking.service';

export interface FavouriteToggleTracking {
    name: string;
    source: string;
    position?: number;
    additional?: Partial<TrackData>;
}

@Component({
    selector: 'ms-favourite-toggle',
    templateUrl: 'favourite-toggle.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class FavouriteToggleComponent implements OnChanges, OnDestroy {
    @Input() id: string;
    @Input() name: string;
    @Input() sport: number;
    @Input() type: FavouriteType;
    @Input() static: boolean;
    @Input() tracking: FavouriteToggleTracking;
    @Input() participantImage?: EventParticipantImage;
    @Output() toggle = new EventEmitter<boolean>();
    @HostBinding('class') className = 'favourite-toggle';

    model?: FavouritesViewModel;
    disabled: boolean;
    toggleClass: boolean;
    flag = false;
    private isEpcotEnabled: boolean;
    private subscription?: Subscription;
    private readonly destroyRef = inject(DestroyRef);
    private readonly addNotificationMessages: Record<FavouriteType, string> = {
        [FavouriteType.League]: this.sitecore.favouritesMessages.AddCompetitionSuccess,
        [FavouriteType.Fixture]: this.sitecore.favouritesMessages.AddEventSuccess,
        [FavouriteType.FixtureV2]: this.sitecore.favouritesMessages.AddEventSuccess,
        [FavouriteType.Participant]: this.sitecore.favouritesMessages.AddParticipantSuccess,
        [FavouriteType.ParticipantV2]: this.sitecore.favouritesMessages.AddParticipantSuccess,
    };
    private readonly removeNotificationMessages: Record<FavouriteType, string> = {
        [FavouriteType.League]: this.sitecore.favouritesMessages.RemoveCompetitionSuccess,
        [FavouriteType.Fixture]: this.sitecore.favouritesMessages.RemoveEventSuccess,
        [FavouriteType.FixtureV2]: this.sitecore.favouritesMessages.RemoveEventSuccess,
        [FavouriteType.Participant]: this.sitecore.favouritesMessages.RemoveParticipantSuccess,
        [FavouriteType.ParticipantV2]: this.sitecore.favouritesMessages.RemoveParticipantSuccess,
    };

    constructor(
        private userService: UserService,
        private favouritesService: FavouritesService,
        private redirectHelper: RedirectHelperService,
        private changeRef: ChangeDetectorRef,
        private trackingService: FavouriteToggleTrackingService,
        public sitecore: Sitecore,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isEpcotEnabled = this.epcotConfigService.isEnabled();
    }

    setToggle(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.flag = true;
        if (!this.userService.isAuthenticated) {
            this.redirectHelper.goToLogin();
        } else {
            if (this.model) {
                this.remove();
            } else {
                this.add();
            }
        }
    }

    ngOnChanges(changes: ISimpleChanges<FavouriteToggleComponent>): void {
        if (changes.static) {
            if (this.static) {
                this.unsubscribe();
            } else {
                this.subscribe();
            }
        }

        this.setState();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }

    private unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }

    private subscribe(): void {
        if (!this.subscription) {
            this.subscription = this.favouritesService.change.subscribe(() => this.setState());
        }
    }

    private remove(): void {
        this.toggleClass = false;
        this.favouritesService
            .remove(this.model!)
            .pipe(this.handleError(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.toggled(this.removeNotificationMessages[this.type]));
    }

    private add(): void {
        this.toggleClass = true;
        this.favouritesService
            .add(this.id, this.sport, this.type, this.name, this.participantImage)
            .pipe(this.handleError(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.toggled(this.addNotificationMessages[this.type], false, this.isEpcotEnabled ? '' : 'theme-success-i'));
    }

    private handleError = () => (source: Observable<any>) =>
        source.pipe(
            map(() => true),
            catchError((error: Error) => {
                this.toggleClass = !!this.model;
                this.toggled(error.message, true);
                return of(false);
            }),
            filter((result) => result),
        );

    private toggled(message: string, toggleError: boolean = false, icon?: string): void {
        this.favouritesService.notify(message, icon);
        this.setState();

        const isToggled = isDefined(this.model);
        this.toggle.emit(isToggled);
        this.changeRef.markForCheck();
        if (!toggleError) {
            this.trackingService.trackToggle(isToggled, this.type, this.tracking);
        }
    }

    private setState(): void {
        this.disabled = !this.userService.isAuthenticated;
        this.model = this.favouritesService.get(this.id, this.sport, this.type);
        if (!this.flag) {
            this.toggleClass = !!this.model;
        }

        this.changeRef.markForCheck();
    }
}
